import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Star, CheckCircle, XCircle, Clock, MessageSquare, Trash2, X } from "lucide-react";
import { apiUrl } from "../../../utils/config";

const API = apiUrl;

const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={14} className={s <= rating ? "text-orange-400 fill-orange-400" : "text-gray-200 fill-gray-200"} />
    ))}
  </div>
);

const statusConfig = {
  Approved: { label: "Approved", cls: "bg-green-100 text-green-700 border-green-200",   icon: CheckCircle },
  Pending:  { label: "Pending",  cls: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock       },
  Rejected: { label: "Rejected", cls: "bg-red-100 text-red-600 border-red-200",          icon: XCircle     },
};

const Reviews = () => {
  const [reviews, setReviews]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [searchTerm, setSearchTerm]       = useState("");
  const [filterStatus, setFilterStatus]   = useState("All");
  const [filterRating, setFilterRating]   = useState("All");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "All") params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;
      const { data } = await axios.get(`${API}/reviews`, { params });
      setReviews(data.list || []);
    } catch {
      setReviews([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterStatus, searchTerm]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/reviews/${id}`, { status }).catch(() => {});
    setReviews((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/reviews/${id}`).catch(() => {});
    setReviews((prev) => prev.filter((r) => r.id !== id));
    setDeleteConfirmId(null);
  };

  const filtered = reviews.filter((r) => {
    const matchRating = filterRating === "All" || r.rating === Number(filterRating);
    return matchRating;
  });

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reviews & Ratings</h1>
        <p className="text-gray-500 text-sm">Moderate and manage customer reviews.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
        {[
          { label: "Total Reviews", value: reviews.length,                                        color: "bg-orange-500", icon: MessageSquare },
          { label: "Approved",      value: reviews.filter((r) => r.status === "Approved").length, color: "bg-green-500",  icon: CheckCircle   },
          { label: "Pending",       value: reviews.filter((r) => r.status === "Pending").length,  color: "bg-yellow-500", icon: Clock         },
          { label: "Avg Rating",    value: avg + " ★",                                            color: "bg-blue-500",   icon: Star          },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className={`p-3 rounded-xl ${s.color}`}>
              <s.icon size={20} className="text-white" />
            </div>
            <div>
              <p className="text-2xl font-extrabold text-gray-900">{s.value}</p>
              <p className="text-sm text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h3 className="font-bold text-lg text-gray-800">All Reviews</h3>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
              {["All", "Approved", "Pending", "Rejected"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    filterStatus === s ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
            >
              <option value="All">All Ratings</option>
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={r}>{r} Star{r !== 1 ? "s" : ""}</option>
              ))}
            </select>
            <div className="relative w-full md:w-52">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-400 text-sm">Loading reviews...</div>
          ) : (
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
                <tr>
                  <th className="p-5">Customer</th>
                  <th className="p-5">Item ID</th>
                  <th className="p-5">Rating</th>
                  <th className="p-5">Comment</th>
                  <th className="p-5">Status</th>
                  <th className="p-5">Date</th>
                  <th className="p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-gray-400">No reviews found.</td></tr>
                )}
                {filtered.map((review) => {
                  const sc = statusConfig[review.status] || statusConfig.Pending;
                  return (
                    <tr key={review.id} className="hover:bg-gray-50 transition">
                      <td className="p-5 font-bold text-gray-900">{review.user_name}</td>
                      <td className="p-5 text-gray-500 text-sm">#{review.menu_item_id}</td>
                      <td className="p-5"><StarDisplay rating={review.rating} /></td>
                      <td className="p-5 text-gray-500 max-w-[220px]">
                        <span className="line-clamp-2 whitespace-normal text-sm">{review.comment || "—"}</span>
                      </td>
                      <td className="p-5">
                        <span className={`flex items-center gap-1 w-fit px-2.5 py-1 rounded-full text-xs font-bold border ${sc.cls}`}>
                          <sc.icon size={12} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="p-5 text-gray-500 text-sm">
                        {review.created_at
                          ? new Date(review.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="p-5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {review.status !== "Approved" && (
                            <button onClick={() => updateStatus(review.id, "Approved")} className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition" title="Approve">
                              <CheckCircle size={16} />
                            </button>
                          )}
                          {review.status !== "Rejected" && (
                            <button onClick={() => updateStatus(review.id, "Rejected")} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition" title="Reject">
                              <XCircle size={16} />
                            </button>
                          )}
                          <button onClick={() => setDeleteConfirmId(review.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-500" />
            </div>
            <h2 className="text-lg font-extrabold text-gray-900">Delete Review?</h2>
            <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition active:scale-95">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
